<template>
  <div class="cashflow">
    <!-- ===== Header ===== -->
    <div class="cf-header">
      <div>
        <h1 class="cf-title">Cash flow</h1>
        <p class="cf-subtitle">Money movement across your bank accounts, from imported bank statements.</p>
      </div>
      <div class="cf-header-actions">
        <CButtonGroup size="sm" class="period-group">
          <CButton
            v-for="p in periodOptions"
            :key="p.value"
            :class="['period-btn', { active: selectedPeriod === p.value }]"
            @click="selectedPeriod = p.value"
          >
            {{ p.label }}
          </CButton>
        </CButtonGroup>
        <button type="button" class="refresh-btn" :disabled="loading" title="Refresh" aria-label="Refresh" @click="fetchAll">
          <CIcon name="cil-reload" :class="{ spin: loading }" />
        </button>
      </div>
    </div>

    <!-- ===== KPI row ===== -->
    <CRow class="kpi-row" :class="{ loading: loading }">
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top">
            <span class="kpi-label">Money in</span>
            <span class="kpi-chip chip-success"><CIcon name="cil-arrow-bottom" /></span>
          </div>
          <div class="kpi-value">{{ formatCurrency(totalIn) }}</div>
          <div class="kpi-sub">{{ periodLabel }} · credits</div>
        </div>
      </CCol>
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top">
            <span class="kpi-label">Money out</span>
            <span class="kpi-chip chip-danger"><CIcon name="cil-arrow-top" /></span>
          </div>
          <div class="kpi-value">{{ formatCurrency(totalOut) }}</div>
          <div class="kpi-sub">{{ periodLabel }} · debits</div>
        </div>
      </CCol>
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top">
            <span class="kpi-label">Net cash flow</span>
            <span class="kpi-chip chip-navy"><CIcon name="cil-chart-line" /></span>
          </div>
          <div class="kpi-value" :class="netFlow < 0 ? 'text-danger-strong' : ''">{{ formatCurrency(netFlow) }}</div>
          <div class="kpi-sub">{{ periodLabel }} · in − out</div>
        </div>
      </CCol>
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top">
            <span class="kpi-label">Transactions</span>
            <span class="kpi-chip chip-info"><CIcon name="cil-list" /></span>
          </div>
          <div class="kpi-value">{{ periodTransactions.length }}</div>
          <div class="kpi-sub">{{ bankStatements.length }} statement{{ bankStatements.length === 1 ? '' : 's' }} imported</div>
        </div>
      </CCol>
    </CRow>

    <!-- ===== Chart + by-bank ===== -->
    <CRow>
      <CCol lg="8">
        <CCard class="cf-card mb-4 h-100">
          <CCardHeader class="cf-card-header">
            <h5 class="cf-card-title mb-0">Cash flow over time</h5>
            <small class="text-muted">Money in vs. out by month</small>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div v-if="loading" class="cf-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="!monthlySeries.labels.length" class="cf-empty">
              <CIcon name="cil-chart-line" size="2xl" class="text-muted mb-2" />
              <div>No transactions for this period.</div>
            </div>
            <ProfitLossChart v-else :chartData="cashFlowChartData" style="height: 320px" />
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4">
        <CCard class="cf-card mb-4 h-100">
          <CCardHeader class="cf-card-header">
            <h5 class="cf-card-title mb-0">By bank account</h5>
            <small class="text-muted">Net flow per account</small>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div v-if="loading" class="cf-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="byBank.length === 0" class="cf-empty">
              <CIcon name="cil-bank" size="2xl" class="text-muted mb-2" />
              <div>No bank activity yet.</div>
            </div>
            <div v-else>
              <div v-for="b in byBank" :key="b.bank" class="bank-row">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="bank-name"><CIcon name="cil-bank" class="mr-2 text-brand-primary" />{{ b.bank }}</div>
                  <div class="font-weight-bold" :class="b.net >= 0 ? 'text-success-strong' : 'text-danger-strong'">
                    {{ formatCurrency(b.net) }}
                  </div>
                </div>
                <div class="bank-bar">
                  <div class="bar-in" :style="{ width: barWidth(b.in, b) + '%' }"></div>
                  <div class="bar-out" :style="{ width: barWidth(b.out, b) + '%' }"></div>
                </div>
                <div class="d-flex justify-content-between small text-muted mt-1">
                  <span>In {{ formatCurrency(b.in) }}</span>
                  <span>Out {{ formatCurrency(b.out) }}</span>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ===== Transactions ===== -->
    <CCard class="cf-card mb-4">
      <CCardHeader class="cf-card-header flex-wrap">
        <div>
          <h5 class="cf-card-title mb-0">Bank transactions</h5>
          <small class="text-muted">Imported from bank statements</small>
        </div>
        <div class="d-flex align-items-center cf-tx-controls">
          <CButtonGroup size="sm" class="mr-2">
            <CButton :class="['filter-btn', { active: txFilter === 'all' }]" @click="txFilter = 'all'">All</CButton>
            <CButton :class="['filter-btn', { active: txFilter === 'in' }]" @click="txFilter = 'in'">In</CButton>
            <CButton :class="['filter-btn', { active: txFilter === 'out' }]" @click="txFilter = 'out'">Out</CButton>
          </CButtonGroup>
          <div class="cf-search">
            <CIcon name="cil-magnifying-glass" class="cf-search-icon" />
            <input v-model="search" type="text" placeholder="Search description…" aria-label="Search transactions" class="cf-search-input" />
          </div>
        </div>
      </CCardHeader>
      <CCardBody class="pt-2 pb-3 px-0">
        <div v-if="loading" class="cf-placeholder"><CSpinner color="primary" /></div>
        <div v-else-if="displayedTransactions.length === 0" class="cf-empty">
          <CIcon name="cil-credit-card" size="2xl" class="text-muted mb-2" />
          <div>No transactions match your filter.</div>
        </div>
        <div v-else class="table-responsive">
          <table class="table cf-table mb-0">
            <thead>
              <tr>
                <th class="pl-4">Date</th>
                <th>Description</th>
                <th>Bank</th>
                <th>Source</th>
                <th class="text-right">Money in</th>
                <th class="text-right pr-4">Money out</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tx in displayedTransactions" :key="tx.id">
                <td class="pl-4 text-nowrap">{{ formatDate(tx.date) }}</td>
                <td>
                  <div class="tx-desc">{{ tx.description || "—" }}</div>
                  <small v-if="tx.remarks" class="text-muted">{{ tx.remarks }}</small>
                </td>
                <td class="text-nowrap">
                  <div>{{ tx.bankName }}</div>
                  <small v-if="tx.bankAccountNo" class="text-muted">{{ tx.bankAccountNo }}</small>
                </td>
                <td>
                  <CBadge :color="tx.source === 'BankStatement' ? 'info' : 'secondary'">
                    {{ tx.source === 'BankStatement' ? 'Statement' : 'Manual' }}
                  </CBadge>
                </td>
                <td class="text-right font-weight-bold text-nowrap text-success-strong">
                  {{ tx.isInflow ? "+" + formatCurrency(tx.amount) : "" }}
                </td>
                <td class="text-right pr-4 font-weight-bold text-nowrap text-danger-strong">
                  {{ !tx.isInflow ? "−" + formatCurrency(tx.amount) : "" }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="cf-table-total">
                <td class="pl-4" colspan="4">Total ({{ displayedTransactions.length }} shown)</td>
                <td class="text-right text-success-strong">{{ formatCurrency(displayedIn) }}</td>
                <td class="text-right pr-4 text-danger-strong">{{ formatCurrency(displayedOut) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CCardBody>
      <CCardFooter v-if="!loading && displayedTransactions.length" class="cf-card-footer">
        <small class="text-muted">Showing {{ displayedTransactions.length }} of {{ periodTransactions.length }} transactions</small>
      </CCardFooter>
    </CCard>

    <!-- ===== Accounts + Statements ===== -->
    <CRow>
      <CCol lg="6">
        <CCard class="cf-card mb-4 h-100">
          <CCardHeader class="cf-card-header">
            <h5 class="cf-card-title mb-0">Bank accounts</h5>
            <CButton size="sm" class="btn-brand-outline" @click="navigateTo('/tenants/BankList')">
              <CIcon name="cil-bank" /> Manage
            </CButton>
          </CCardHeader>
          <CCardBody class="pt-2 pb-3 px-0">
            <div v-if="bankAccounts.length === 0" class="cf-empty">
              <CIcon name="cil-bank" size="2xl" class="text-muted mb-2" />
              <div>No bank accounts yet.</div>
            </div>
            <div v-else class="table-responsive">
              <table class="table cf-table mb-0">
                <tbody>
                  <tr v-for="acc in bankAccounts" :key="acc.id">
                    <td class="pl-4">
                      <router-link :to="'/tenants/Bank/' + acc.id" class="font-weight-semibold d-block">{{ acc.name }}</router-link>
                      <small class="text-muted">{{ acc.accountNo }}</small>
                    </td>
                    <td class="text-right pr-4">
                      <div class="font-weight-bold">{{ formatCurrency(acc.balance) }}</div>
                      <small class="text-muted">{{ acc.accountName }}</small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6">
        <CCard class="cf-card mb-4 h-100">
          <CCardHeader class="cf-card-header">
            <h5 class="cf-card-title mb-0">Imported statements</h5>
            <small class="text-muted">{{ bankStatements.length }} total</small>
          </CCardHeader>
          <CCardBody class="pt-2 pb-3 px-0">
            <div v-if="bankStatements.length === 0" class="cf-empty">
              <CIcon name="cil-file" size="2xl" class="text-muted mb-2" />
              <div>No bank statements imported yet.</div>
            </div>
            <div v-else class="table-responsive">
              <table class="table cf-table mb-0">
                <tbody>
                  <tr v-for="st in sortedStatements" :key="st.id">
                    <td class="pl-4">
                      <div class="font-weight-semibold">{{ (st.bank && st.bank.name) || "Bank statement" }}</div>
                      <small class="text-muted">{{ formatDate(st.date) }}</small>
                    </td>
                    <td class="text-right pr-4">
                      <a v-if="st.documentId" :href="statementDocUrl(st)" target="_blank" class="text-brand-accent">
                        <CIcon name="cil-cloud-download" class="mr-1" />View
                      </a>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { freeSet } from "@coreui/icons";
import { mytToday, asDateString, addDays, formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import ProfitLossChart from "../widgets/ProfitLossChart.vue";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad2 = (n) => String(n).padStart(2, "0");
import ProfileApi from "@/lib/profileApi";
import TransactionApi from "@/lib/transactionApi";
import BankStatementApi from "@/lib/bankStatementApi";
import BankApi from "@/lib/bankApi";

const BRAND = { navy: "#1E3A5F", accent: "#10B981", danger: "#EF4444" };

export default {
  name: "DashboardCashFlow",
  freeSet,
  components: { ProfitLossChart },
  data() {
    return {
      profileApi: new ProfileApi(),
      transactionApi: new TransactionApi(),
      bankStatementApi: new BankStatementApi(),
      bankApi: new BankApi(),

      loading: false,
      company: {},

      transactionsRaw: [],
      bankStatements: [],
      bankAccounts: [],

      selectedPeriod: "all",
      periodOptions: [
        { value: "month", label: "Month" },
        { value: "quarter", label: "Quarter" },
        { value: "year", label: "Year" },
        { value: "all", label: "All" },
      ],
      txFilter: "all",
      search: "",
    };
  },

  mounted() {
    this.$store.commit("setPageNavItems", ["Dashboard", "Cash flow"]);
    this.fetchAll();
  },

  computed: {
    businessCurrency() {
      const b = (typeof auth !== "undefined" && auth.getLocalBusiness && auth.getLocalBusiness()) || {};
      return this.company.currency || b.currency || "MYR";
    },
    periodLabel() {
      return { month: "This month", quarter: "This quarter", year: "This year", all: "All time" }[this.selectedPeriod] || "All time";
    },

    statementBankMap() {
      const map = {};
      this.bankStatements.forEach((s) => {
        map[s.id] = {
          name: (s.bank && s.bank.name) || "Bank statement",
          accountNo: (s.bank && s.bank.accountNo) || "",
        };
      });
      return map;
    },

    // Normalised transactions (signed amount → inflow/outflow + bank name & account)
    transactions() {
      return this.transactionsRaw.map((t) => {
        const signed = Number(t.amount) || 0;
        const isInflow = t.transactionType === 0 || (t.transactionType == null && signed > 0);
        const stmtBank = this.statementBankMap[t.sourceBankStatementId];
        const txBank = t.bankStatement && t.bankStatement.bank;
        return {
          id: t.id,
          date: t.date,
          description: t.description,
          remarks: t.remarks,
          amount: Math.abs(signed),
          isInflow,
          source: t.transactionSourceTypeDescription,
          bankName: (stmtBank && stmtBank.name) || (txBank && txBank.name) || "Manual entry",
          bankAccountNo: (stmtBank && stmtBank.accountNo) || (txBank && txBank.accountNo) || "",
        };
      });
    },

    periodRange() {
      if (this.selectedPeriod === "all") return null;
      // Inclusive "yyyy-MM-dd" range in Malaysia time.
      const [y, m] = mytToday().split("-").map(Number);
      let startMonth = m;
      let months = 1;
      if (this.selectedPeriod === "quarter") {
        startMonth = Math.floor((m - 1) / 3) * 3 + 1;
        months = 3;
      } else if (this.selectedPeriod === "year") {
        startMonth = 1;
        months = 12;
      }
      const endIdx = y * 12 + (startMonth - 1) + months; // first month after the range
      const after = `${Math.floor(endIdx / 12)}-${pad2((endIdx % 12) + 1)}-01`;
      return { start: `${y}-${pad2(startMonth)}-01`, end: addDays(after, -1) };
    },
    periodTransactions() {
      if (!this.periodRange) return this.transactions;
      return this.transactions.filter((t) => {
        const d = asDateString(t.date);
        return !!d && d >= this.periodRange.start && d <= this.periodRange.end;
      });
    },

    totalIn() {
      return this.periodTransactions.filter((t) => t.isInflow).reduce((s, t) => s + t.amount, 0);
    },
    totalOut() {
      return this.periodTransactions.filter((t) => !t.isInflow).reduce((s, t) => s + t.amount, 0);
    },
    netFlow() {
      return this.totalIn - this.totalOut;
    },

    monthlySeries() {
      const buckets = {};
      this.periodTransactions.forEach((t) => {
        const d = asDateString(t.date);
        if (!d) return;
        const key = d.substring(0, 7);
        if (!buckets[key]) buckets[key] = { in: 0, out: 0 };
        if (t.isInflow) buckets[key].in += t.amount;
        else buckets[key].out += t.amount;
      });
      const keys = Object.keys(buckets).sort().slice(-12);
      return {
        labels: keys.map((k) => `${MONTH_LABELS[Number(k.substring(5, 7)) - 1]} '${k.substring(2, 4)}`),
        inData: keys.map((k) => buckets[k].in),
        outData: keys.map((k) => buckets[k].out),
        netData: keys.map((k) => buckets[k].in - buckets[k].out),
      };
    },
    cashFlowChartData() {
      const s = this.monthlySeries;
      const ds = (label, data, color) => ({
        label,
        backgroundColor: this.hexToRgba(color, 0.18),
        borderColor: color,
        pointBackgroundColor: color,
        pointBorderColor: "#fff",
        data,
      });
      return {
        labels: s.labels,
        datasets: [
          ds("Money in", s.inData, BRAND.accent),
          ds("Money out", s.outData, BRAND.danger),
          ds("Net", s.netData, BRAND.navy),
        ],
      };
    },

    byBank() {
      const m = {};
      this.periodTransactions.forEach((t) => {
        if (!m[t.bankName]) m[t.bankName] = { bank: t.bankName, in: 0, out: 0, count: 0 };
        if (t.isInflow) m[t.bankName].in += t.amount;
        else m[t.bankName].out += t.amount;
        m[t.bankName].count++;
      });
      return Object.values(m)
        .map((b) => ({ ...b, net: b.in - b.out }))
        .sort((a, b) => b.in + b.out - (a.in + a.out));
    },

    displayedTransactions() {
      let rows = this.periodTransactions;
      if (this.txFilter === "in") rows = rows.filter((t) => t.isInflow);
      else if (this.txFilter === "out") rows = rows.filter((t) => !t.isInflow);
      const q = this.search.trim().toLowerCase();
      if (q) rows = rows.filter((t) => (t.description || "").toLowerCase().includes(q) || (t.bankName || "").toLowerCase().includes(q));
      return [...rows].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 100);
    },
    displayedIn() {
      return this.displayedTransactions.filter((t) => t.isInflow).reduce((s, t) => s + t.amount, 0);
    },
    displayedOut() {
      return this.displayedTransactions.filter((t) => !t.isInflow).reduce((s, t) => s + t.amount, 0);
    },

    sortedStatements() {
      return [...this.bankStatements].sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },

  methods: {
    async fetchAll() {
      this.loading = true;
      const unwrap = (res) => (res && (res.result && res.result.data ? res.result.data : res.result)) || [];
      const results = await Promise.allSettled([
        this.transactionApi.getListByCurrentBusiness(),
        this.bankStatementApi.getListByCurrentBusiness(),
        this.bankApi.getListByCurrentBusiness(),
        this.profileApi.getCurrentProfile(),
      ]);
      const [tx, st, bk, pf] = results;
      if (tx.status === "fulfilled") this.transactionsRaw = unwrap(tx.value);
      if (st.status === "fulfilled") this.bankStatements = unwrap(st.value);
      if (bk.status === "fulfilled") this.bankAccounts = unwrap(bk.value);
      if (pf.status === "fulfilled" && pf.value && pf.value.result) {
        const biz = pf.value.result.defaultBusiness || {};
        this.company = { currency: biz.currency || "" };
      }
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) {
        failed.forEach((r) => console.error("Cash flow load failed", r.reason));
        this.$toast.warning("Some cash flow figures couldn't be loaded, so totals may be incomplete. Refresh to try again.");
      }
      this.loading = false;
    },

    barWidth(value, b) {
      const max = Math.max(b.in, b.out, 1);
      return Math.round((value / max) * 100);
    },
    hexToRgba(hex, alpha) {
      const h = hex.replace("#", "");
      return `rgba(${parseInt(h.substr(0, 2), 16)}, ${parseInt(h.substr(2, 2), 16)}, ${parseInt(h.substr(4, 2), 16)}, ${alpha})`;
    },
    statementDocUrl(st) {
      const base = typeof apiUrl !== "undefined" ? apiUrl : "";
      return base + "documents/file/" + st.documentId;
    },
    formatCurrency(amount) {
      const val = Number(amount) || 0;
      const cur = this.businessCurrency || "MYR";
      if (cur === "MYR") return val < 0 ? `-RM ${formatMyr(-val)}` : `RM ${formatMyr(val)}`;
      try {
        return new Intl.NumberFormat("en-MY", { style: "currency", currency: cur }).format(val);
      } catch (e) {
        return `${cur} ${formatMyr(val)}`;
      }
    },
    formatDate(date) {
      return formatDate(date) || "—";
    },
    navigateTo(url) {
      if (url && this.$route.path !== url) this.$router.push(url).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* ===== Header ===== */
.cf-header {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.75rem;
}
.cf-title { font-size: 1.75rem; font-weight: 800; letter-spacing: -0.02em; color: var(--marketing-primary); margin: 0 0 0.25rem; }
.cf-subtitle { color: var(--marketing-gray-500); margin: 0; }
.cf-header-actions { display: flex; align-items: center; gap: 0.5rem; }
.period-group .period-btn {
  border: 1px solid var(--marketing-gray-300);
  background: #fff; color: var(--marketing-gray-600); font-weight: 600;
}
.period-group .period-btn.active { background: var(--marketing-primary); border-color: var(--marketing-primary); color: #fff; }
.refresh-btn {
  width: 34px; height: 34px; border-radius: 8px;
  border: 1px solid var(--marketing-gray-300); background: #fff; color: var(--marketing-gray-600);
}
.refresh-btn:hover:not(:disabled) { color: var(--marketing-accent); border-color: var(--marketing-accent); }
.refresh-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== KPI ===== */
.kpi-row.loading { opacity: 0.6; pointer-events: none; }
.kpi-card {
  background: #fff; border: 1px solid var(--marketing-gray-200); border-radius: 14px;
  padding: 1.25rem 1.3rem; height: 100%;
}
.kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
.kpi-label { font-size: 0.85rem; font-weight: 600; color: var(--marketing-gray-500); }
.kpi-chip { width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center; font-size: 1.05rem; }
.chip-navy { background: rgba(30, 58, 95, 0.10); color: var(--marketing-primary); }
.chip-success { background: rgba(16, 185, 129, 0.13); color: var(--marketing-accent-dark); }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: var(--marketing-danger); }
.chip-info { background: rgba(59, 130, 246, 0.12); color: var(--marketing-info); }
.kpi-value { font-size: 1.6rem; font-weight: 800; line-height: 1.05; letter-spacing: -0.02em; color: var(--marketing-primary); }
.kpi-sub { font-size: 0.74rem; color: var(--marketing-gray-400); margin-top: 0.4rem; }

/* ===== Cards ===== */
.cf-card { border: 1px solid var(--marketing-gray-200); border-radius: 14px; }
.cf-card-header {
  background: transparent; border-bottom: 1px solid var(--marketing-gray-100);
  padding: 1.1rem 1.5rem 0.9rem; display: flex; align-items: center; justify-content: space-between;
}
.cf-card-title { font-size: 1.05rem; font-weight: 700; color: var(--marketing-primary); }
.cf-card-footer { background: transparent; border-top: 1px solid var(--marketing-gray-100); }
.text-brand-primary { color: var(--marketing-primary) !important; }
.text-brand-accent { color: var(--marketing-accent) !important; }
.text-success-strong { color: var(--marketing-accent-dark) !important; }
.text-danger-strong { color: var(--marketing-danger) !important; }
.btn-brand-outline { border: 1px solid var(--marketing-primary); color: var(--marketing-primary); background: #fff; font-weight: 600; }
.btn-brand-outline:hover { background: var(--marketing-primary); color: #fff; }

.cf-placeholder { height: 220px; display: flex; align-items: center; justify-content: center; }
.cf-empty { text-align: center; padding: 2.5rem 1rem; color: var(--marketing-gray-500); }

/* ===== By bank ===== */
.bank-row { padding: 0.6rem 0; border-bottom: 1px solid var(--marketing-gray-100); }
.bank-row:last-child { border-bottom: none; }
.bank-name { font-weight: 600; color: var(--marketing-gray-800); font-size: 0.9rem; }
.bank-bar { display: flex; height: 6px; border-radius: 4px; overflow: hidden; background: var(--marketing-gray-100); }
.bar-in { background: var(--marketing-accent); }
.bar-out { background: var(--marketing-danger); }

/* ===== Table ===== */
.cf-table th {
  font-size: 0.8rem; color: var(--marketing-gray-500);
  border-top: none; border-bottom: 1px solid var(--marketing-gray-200); padding-top: 0.5rem; padding-bottom: 0.5rem;
}
.cf-table td { vertical-align: middle; border-top: 1px solid var(--marketing-gray-100); }
.cf-table tfoot .cf-table-total td {
  border-top: 2px solid var(--marketing-gray-200);
  font-weight: 800;
  color: var(--marketing-gray-700);
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
}
.tx-desc { font-weight: 500; color: var(--marketing-gray-800); max-width: 360px; }

/* ===== Tx controls ===== */
.cf-tx-controls { gap: 0.5rem; }
.filter-btn { border: 1px solid var(--marketing-gray-300); background: #fff; color: var(--marketing-gray-600); font-weight: 600; }
.filter-btn.active { background: var(--marketing-primary); border-color: var(--marketing-primary); color: #fff; }
.cf-search { position: relative; }
.cf-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--marketing-gray-400); }
.cf-search-input {
  border: 1px solid var(--marketing-gray-300); border-radius: 8px; padding: 0.35rem 0.6rem 0.35rem 2rem;
  font-size: 0.85rem; outline: none; transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.cf-search-input:focus { border-color: var(--marketing-accent); box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15); }

.cursor-pointer { cursor: pointer; }

@media (max-width: 575.98px) {
  .cf-title { font-size: 1.4rem; }
  .cf-tx-controls { width: 100%; margin-top: 0.5rem; }
  .cf-search, .cf-search-input { width: 100%; }
}
</style>
